import { Box, Button, emphasize, Grid, Stack, Typography } from "@mui/material";
import { FC, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Helmet } from 'react-helmet-async'
import { push } from "connected-react-router";
import WithSave from "@/controller/WithSave";
import { InfoTypeInput, InfoTypeResponseDto } from "@/gen/api/secureai";
import { OdResourcePageProps } from "@/types/resource";
import InfoTypeEditor from "@/components/secureai/InfoTypeEditor";
import { RecPatch } from "@/dataProvider/tools";
import Edit from "@/controller/Edit";
import { useDeleteWithConfirmController, useEditContext } from "ra-core";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import Confirm from "@/components/widgets/Confirm";
import usePermitGuard from "@/authProvider/usePermitGuard";
import { MANAGE_SECUREAI_SETTINGS } from "@/constants";
import { useCreateZTreeStore } from '@/utils/zustand-form/store';
import { ZTreeStoreProvider } from '@/utils/zustand-form/store.context';
import { CollectEditsProtocol } from '@/utils/zustand-form/ext/collectEdits';
import { useStore } from "zustand";
import { FormNode } from "@/utils/zustand-form/types";




const SecAiEditInfoType: FC<OdResourcePageProps&RecPatch> = (props) => {
  const { id, resource } = props
  const handlers = useDefaultSavedHandlers("Edit Info Type")
  usePermitGuard([[MANAGE_SECUREAI_SETTINGS]])
  return (
    <Edit resource={resource} basePath="" id={id} mutationMode="pessimistic" {...handlers}>
      <EditInfoTypeBody {...props} />
    </Edit>
  )
}

const EditInfoTypeBody: FC<OdResourcePageProps&RecPatch> = ({
  id, options, resource,
}) => {
  const { record, refetch } = useEditContext<InfoTypeResponseDto&RecPatch>()
  const dispatch = useDispatch()

  // Create zstore for InfoTypeEditor
  const zstore = useCreateZTreeStore();

  const handleSave = (save: (record:any,callbacks?:any)=>Promise<void>) => async () => {
    try {
      // Use CollectEditsProtocol to collect data from zustand store
      const payload = await CollectEditsProtocol.invoke<InfoTypeInput>(
        (protocol) => zstore.getState().invokeNodeProtocol('root', protocol) as any
      );
      (payload as any).id = id;

      await save(payload)
      refetch?.();
    } catch (error) {
      return;
    }
  };
  const dirty = useStore(zstore, state => {
    let hasChanges = Object.values(state.getNode('root')?.state.touched ?? {}).some(x => x)
    if (hasChanges) { return true }
    state.traverseDescendants('root', (next) => {
      let childNode : FormNode|null = null 
      while (!hasChanges && (childNode = next())) {
        hasChanges = Object.values(childNode.state.touched).some(x => x)
      }
    })
    return hasChanges
  })

  const delHandlers = useDefaultSavedHandlers('Delete Info Type')
  delHandlers.onSuccess.merge(useCallback(() => { dispatch(push(`/${resource}`)) }, [dispatch, resource]))
  const {
    open: deleteDialogOpen,
    loading,
    handleDialogOpen,
    handleDialogClose,
    handleDelete,
  } = useDeleteWithConfirmController({
    resource,
    record: { id },
    mutationMode: 'pessimistic',
    ...delHandlers,
  })

  return (
    <>
      <Helmet>
        <title>Edit Info Type | OnData</title>
      </Helmet>
      <Grid container spacing={4} >
        <Grid item xs={12}>
          <ZTreeStoreProvider value={zstore}>
            <InfoTypeEditor title="Edit Info Type"
              action={
                <Stack direction="row" spacing={2}>
                  <Button color="primary" variant="outlined" component={RouterLink} size="small"
                    to={`/${resource}`}
                  >
                    Cancel
                  </Button>
                  <WithSave async
                    render={({save, saving}) =>
                      <Button color="primary" variant="contained" size="small"
                        disabled={saving || !dirty}
                        onClick={handleSave(save)}
                        >
                        Save
                      </Button>}
                  />
                  {!record?.isBuiltIn &&
                    <Button color="error" variant="outlined" size="small"
                      sx={{ 
                        ml: theme => `${theme.spacing(4)} !important`,
                        color: theme => emphasize(theme.palette.error.main, 0.35),  
                      }}
                      onClick={handleDialogOpen}
                    >
                      Delete
                    </Button>
                  }
                </Stack>
              }
            />
          </ZTreeStoreProvider>
        </Grid>
      </Grid>
      <Confirm
        isOpen={deleteDialogOpen}
        title={"Delete Info Type"}
        loading={loading}
        onConfirm={handleDelete}
        onClose={handleDialogClose}
        confirm="Confirm"
        cancel="Cancel"
        content={(
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1 }}
              >
                Please confirm whether to proceed with the deletion of this
                {(record?.name) ? ` "${record?.name}" ` : " "}
                Info Type.
              </Typography>
            </Box>
          </Box>
        )}
      />
    </>
  )
}

export default SecAiEditInfoType;

